import {
    GridApi,
    GridCellParams,
    GridColDef,
    GridGroupingColDefOverride,
    GridGroupingColDefOverrideParams,
    GridRowId,
    GridValueGetterParams,
    GridValueFormatterParams,
    GridExpandMoreIcon,
    GridKeyboardArrowRight,
    useGridApiContext,
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    GridBasicGroupNode,
} from '@enterprise/material-data-grid';
import type { GridValidRowModel } from '@mui/x-data-grid-premium';
import { MutableRefObject, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import i18n from '../../../../../i18n';
import styles from '../combinedItemsGrid.module.scss';
import classNames from 'classnames';
import { SpotSvg } from '@enterprise/spot';
import { CombinedItem } from '../../../../../core/models';
import IconButton from '@mui/material/IconButton';
import { SiteEntity } from '../../../../../core/entity/SiteEntity';
import { useStores } from '../../../../../hooks';

export enum CombinedItemsGridColumn {
    MobileView = 'mobileView',
    Id = 'pimsId',
    Description = 'description',
    HideZeroAmounts = 'hideZeroAmounts',
    Products = 'products',
    Practice = 'site1',
}

enum CellType {
    Link = 'link',
    Text = 'text',
    Pill = 'pill',
}

interface HeaderProps {
    label?: string | ReactElement;
}

interface CellProps {
    type?: CellType;
}

export const header = (props: HeaderProps) =>
    function Header(): ReactElement {
        const { label } = props;

        return (
            <div className={styles.headerContainer}>
                <div className={classNames('spot-typography__text--body', styles.headerCell)}>{label}</div>
            </div>
        );
    };

function CellProducts<V extends GridValidRowModel>(params: GridCellParams<V, CombinedItem>): ReactElement {
    const { formattedValue, id, rowNode } = params;
    const apiRef = useGridApiContext();
    const expandedRowIds = apiRef.current.getExpandedDetailPanels();
    const [expanded, setExpanded] = useState(expandedRowIds.includes(id));

    return (
        <div
            className={styles.cellContainer}
            onClick={(event) => {
                if (expanded) {
                    apiRef.current.setExpandedDetailPanels(expandedRowIds.filter((expandedId) => expandedId !== id));
                } else {
                    apiRef.current.setExpandedDetailPanels([...expandedRowIds, id]);
                }

                setExpanded(!expanded);
                event.stopPropagation();

                // if ((rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                //     event.stopPropagation();
                // }
            }}
        >
            <div className={classNames(styles.productCell, {})}>
                {!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated && (
                    <IconButton className={styles.expandIcon} size={'small'} tabIndex={-1} aria-label="Expand row">
                        {expanded ? <GridExpandMoreIcon /> : <GridKeyboardArrowRight />}
                    </IconButton>
                )}
                <span>{formattedValue as unknown as ReactNode}</span>
            </div>
        </div>
    );
}

export const cell = (props: CellProps = {}) =>
    function Cell<V extends GridValidRowModel>(params: GridCellParams<V, CombinedItem>): ReactElement {
        const { formattedValue } = params;

        return (
            <div className={styles.cellContainer}>
                <div className={classNames(styles.cell, {})}>
                    <span>{formattedValue as unknown as ReactNode}</span>
                </div>
            </div>
        );
    };

export const getGroupItems = (apiRef: MutableRefObject<GridApi>, rowId: GridRowId): CombinedItem[] => {
    const { dataRowIdToModelLookup, tree } = apiRef.current?.state.rows;
    const groupRowIds: GridRowId[] | undefined = (tree?.[rowId] as GridBasicGroupNode)?.children;

    return groupRowIds.map((id) => dataRowIdToModelLookup[id]) as CombinedItem[];
};

export interface UseItemGridColumnsParams {
    apiRef: MutableRefObject<GridApi>;
}

export interface ItemGridColumnsDetails {
    isAutoGenerated: (rowId: GridRowId) => boolean | undefined;
    columnNames: Map<CombinedItemsGridColumn, string>;
    columnSizes: Map<CombinedItemsGridColumn, number>;
    columns: GridColDef<CombinedItem>[];
    groupColumn: (params: GridGroupingColDefOverrideParams) => GridGroupingColDefOverride<CombinedItem>;
}

export const useCombinedItemGridColumns = (params: UseItemGridColumnsParams): ItemGridColumnsDetails => {
    const {
        domain: { constantsStore },
    } = useStores();

    const { apiRef } = params;
    const isAutoGenerated = (rowId: GridRowId): boolean | undefined => {
        const node = apiRef.current.getRowNode(rowId) as { isAutoGenerated?: boolean };
        return node?.isAutoGenerated;
    };

    const sitesMap = useMemo(
        () => new Map<string, SiteEntity>(constantsStore.applications.map((item) => [item.provider.siteId, item])),
        [constantsStore.applications],
    );

    const columnNames = useMemo(
        () =>
            new Map<CombinedItemsGridColumn, string>([
                [CombinedItemsGridColumn.MobileView, i18n.t('controlCenter:name', 'Name')],
                [CombinedItemsGridColumn.Id, i18n.t('controlCenter:itemId', 'Id')],
                [CombinedItemsGridColumn.Description, i18n.t('controlCenter:practiceDescription', 'Description')],
                [CombinedItemsGridColumn.Practice, i18n.t('controlCenter:practice', 'Practice')],
                [CombinedItemsGridColumn.HideZeroAmounts, i18n.t('controlCenter:hideZeroAmounts', 'Hide Zero Amounts')],
                [CombinedItemsGridColumn.Products, i18n.t('controlCenter:products', 'Products')],
            ]),
        [],
    );

    const columnSizes = useMemo(
        () =>
            new Map<CombinedItemsGridColumn, number>([
                [CombinedItemsGridColumn.Id, 150],
                [CombinedItemsGridColumn.Description, 500],
                [CombinedItemsGridColumn.Practice, 360],
                [CombinedItemsGridColumn.HideZeroAmounts, 200],
                [CombinedItemsGridColumn.Products, 120],
            ]),
        [],
    );

    const columns = useMemo<GridColDef<CombinedItem>[]>(() => {
        return [
            {
                field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
                renderCell: CellProducts,
                minWidth: columnSizes.get(CombinedItemsGridColumn.Products),
                width: columnSizes.get(CombinedItemsGridColumn.Products),
                headerName: columnNames.get(CombinedItemsGridColumn.Products),
                description: columnNames.get(CombinedItemsGridColumn.Products),
                renderHeader: header({ label: columnNames.get(CombinedItemsGridColumn.Products) }),
                sortable: true,
                resizable: true,
                disableColumnMenu: false,
                valueGetter({ row, rowNode, id }: GridValueGetterParams<CombinedItem, string>) {
                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return row.itemsCount;
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = items[0].itemsCount;
                    const counts = items.map((item) => item.itemsCount);
                    const containsOne = counts.every((item) => item === first);

                    if (containsOne) {
                        return first;
                    } else {
                        const max = Math.max(...counts);
                        const min = Math.min(...counts);
                        return `${min} - ${max}`;
                    }
                },
            },

            {
                field: CombinedItemsGridColumn.Practice,
                headerName: columnNames.get(CombinedItemsGridColumn.Practice),
                width: columnSizes.get(CombinedItemsGridColumn.Practice),
                disableColumnMenu: false,
                renderHeader: header({ label: columnNames.get(CombinedItemsGridColumn.Practice) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItem, string>) => {
                    const getValue = (item: CombinedItem) => sitesMap.get(item?.provider.siteId)?.name;

                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return getValue(row) || '';
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = getValue(items[0]);
                    const containsOne = items.every((item) => getValue(item) === first);

                    if (containsOne) {
                        return first;
                    } else {
                        return '--';
                    }
                },
            },

            {
                field: CombinedItemsGridColumn.Id,
                headerName: columnNames.get(CombinedItemsGridColumn.Id),
                width: columnSizes.get(CombinedItemsGridColumn.Id),
                disableColumnMenu: false,
                renderHeader: header({ label: columnNames.get(CombinedItemsGridColumn.Id) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItem, string>) => {
                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return row.pimsId || '';
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = items[0];
                    const containsOne = items.every((item) => item.pimsGeneratedId === first.pimsGeneratedId);

                    if (containsOne) {
                        return first.pimsId;
                    }
                    if (items.filter((e) => e.pimsId === first.pimsId).length === items.length) {
                        return first.pimsId;
                    }
                    return '--';
                },
            },
            {
                field: CombinedItemsGridColumn.Description,
                headerName: columnNames.get(CombinedItemsGridColumn.Description),
                width: columnSizes.get(CombinedItemsGridColumn.Description),
                disableColumnMenu: false,
                renderHeader: header({ label: columnNames.get(CombinedItemsGridColumn.Description) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItem, string>) => {
                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return row.description;
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = items[0].description;
                    const containsOne = items.every((item) => item.description === first);

                    if (containsOne) {
                        return first;
                    } else {
                        return '--';
                    }
                },
            },
            {
                field: CombinedItemsGridColumn.HideZeroAmounts,
                headerName: columnNames.get(CombinedItemsGridColumn.HideZeroAmounts),
                width: columnSizes.get(CombinedItemsGridColumn.HideZeroAmounts),
                disableColumnMenu: false,
                groupable: false,
                renderHeader: header({ label: columnNames.get(CombinedItemsGridColumn.HideZeroAmounts) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItem, string>) => {
                    const getValue = (value?: boolean) => (value ? 'Hide Zero Amounts' : '');

                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return getValue(row.hideZeroAmounts);
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = items[0].hideZeroAmounts;
                    const containsOne = items.every((item) => item.hideZeroAmounts === first);

                    if (containsOne) {
                        return getValue(first);
                    } else {
                        return '--';
                    }
                },
            },
        ] as GridColDef<CombinedItem>[];
    }, [apiRef, apiRef.current]);

    const groupColumn = useCallback(
        (params: GridGroupingColDefOverrideParams): GridGroupingColDefOverride<CombinedItem> => {
            const { fields } = params;
            const width = fields.includes(CombinedItemsGridColumn.Description) ? 500 : 150;
            const labels = fields.reduce((agg, field) => {
                const name = columnNames.get(field as CombinedItemsGridColumn);
                return (name && [...agg, name]) || agg;
            }, [] as string[]);
            const headerName = labels.join('/');

            return {
                width,
                headerName,
                renderHeader: header({
                    label: (
                        <>
                            {headerName}
                            <SpotSvg className={styles.titleIcon} icon="list" width="14" />
                        </>
                    ),
                }),
                filterable: true,
                valueFormatter: (params: GridValueFormatterParams) => {
                    const { value } = params;

                    return <span className={styles.group}>{value}</span>;
                },
            };
        },
        [apiRef, apiRef.current],
    );

    return {
        isAutoGenerated,
        columnNames,
        columnSizes,
        columns,
        groupColumn,
    };
};
